/**
 * Seeded RNG (mulberry32). Same seed, same sequence, on every device.
 * Every random outcome in the game must come from one of these.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [min, max], both inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Percentile roll (GDD section 7): true when next() x 100 < chance. */
  roll(chance: number): boolean {
    return this.rollDetailed(chance).hit;
  }

  /** Same as roll(), but also returns the 0 to 100 value rolled, for the result card. */
  rollDetailed(chance: number): { roll: number; hit: boolean } {
    const roll = this.next() * 100;
    return { roll, hit: roll < chance };
  }

  /** Picks a key from a weight table, in the table's key order. */
  weighted<K extends string>(weights: Record<K, number>): K {
    const keys = Object.keys(weights) as K[];
    const total = keys.reduce((s, k) => s + weights[k], 0);
    let r = this.next() * total;
    for (const k of keys) {
      r -= weights[k];
      if (r < 0) return k;
    }
    return keys[keys.length - 1] as K;
  }
}

/** Turns a match id or any string into a 32-bit seed (FNV-1a). */
export function seedFrom(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
